import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { AUTO_STYLE, keyframes, style } from '@angular/animations';

import { NgStaticAnimationService } from '../../shared/services/static-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function rotateOpposite(rotate: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	rotate = rotate ? rotate : 4;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 				: AUTO_STYLE,
				transform 				: `rotate(-${rotate}deg)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 0
			}),
			style({
				transform 				: 'rotate(0deg)',
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 1
			})
		]),
		end		: keyframes([
			style({
				visibility 				: AUTO_STYLE,
				transform 				: 'rotate(0deg)',
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 0
			}),
			style({
				transform 				: `rotate(-${rotate}deg)`,
				'transition-property' 	: 'transform',
				easing 					: 'ease',
				offset 					: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Rotate Opposite in Direction on Event
 */
@Directive({
    selector: '[ngRotateOppositeOnEvent]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgRotateOppositeOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the rotate value is relative to the animation functionality
		if (this.effectOptions?.rotate && this.effectOptions?.rotate < 1) throw new Error(`RotateOppositeOnEventDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.effectOptions?.rotate}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: rotateOpposite(this.effectOptions?.rotate),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Rotate Opposite in Direction on Mouse Hover
 */
@Directive({
    selector: '[ngRotateOppositeOnMouseHover]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgRotateOppositeOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement						= null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the rotate value is relative to the animation functionality
		if (this.timingOptions?.rotate && this.timingOptions?.rotate < 1) throw new Error(`RotateOppositeOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.timingOptions?.rotate}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: rotateOpposite(this.timingOptions?.rotate),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	rotate : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	rotate : number;
}
